/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { DatePipe } from '@angular/common'
import { AfterViewInit, Component, ViewChild, DestroyRef, inject } from '@angular/core'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { fromEvent, debounceTime, distinctUntilChanged, tap, filter } from 'rxjs'
import { Recurso } from 'src/app/models/recurso'
import { LocalStorageService } from 'src/app/services/local-storage.service'
import { RecursoService } from 'src/app/services/recurso.service'
import { SearchInputComponent, DefaultPaginatorComponent } from 'src/app/modules/shared'
import { UnespCoreAuthService } from 'src/libs/unesp-core/services/unesp-core-auth/unesp-core-auth.service'
import { UnespCoreMessageService } from 'src/libs/unesp-core/services/unesp-core-message/unesp-core-message.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-lista-recurso',
  templateUrl: './lista-recurso.component.html',
  styleUrls: ['./lista-recurso.component.css'],
})
export class ListaRecursoComponent implements AfterViewInit {
  private destroyRef = inject(DestroyRef)

  @ViewChild('paginator') paginator!: DefaultPaginatorComponent
  @ViewChild(MatSort) sort!: MatSort
  @ViewChild('searchInput') searchInput!: SearchInputComponent
  totalElements: number = 0
  sortField = 'id'
  sortDirection = 'desc'

  colunasGrid: string[] = ['id', 'concurso', 'nome', 'tipoRecurso', 'data', 'parecer', 'action']
  dataSource: MatTableDataSource<Recurso> = new MatTableDataSource()

  datepipe: DatePipe = new DatePipe('pt-BR')

  constructor(private recursoService: RecursoService, private localStorageService: LocalStorageService, private unespCoreAuthService: UnespCoreAuthService, private unespCoreMessageService: UnespCoreMessageService) {}

  ngAfterViewInit() {
    // server-side search
    fromEvent<KeyboardEvent>(this.searchInput.inputRef.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        filter((e: KeyboardEvent) => e.keyCode === 13),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0
          this.getData()
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe()

    // on sort change, reset to first page and reload
    this.sort.sortChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.sortField = this.sort.active
      this.sortDirection = this.sort.direction || 'asc'
      this.paginator.pageIndex = 0
      this.getData()
    })

    // on paginate events, load a new page
    this.paginator?.page.pipe(tap(() => this.getData()), takeUntilDestroyed(this.destroyRef)).subscribe()

    setTimeout(() => {
      const fromResumo = this.localStorageService.get('lista-recurso.fromResumo');
      if (fromResumo) { this.searchInput.inputRef.nativeElement.value = fromResumo; this.localStorageService.set('lista-recurso.fromResumo', null); }


      this.getData()
    }, 0)
  }

  private getData() {
    this.recursoService
      .listar(this.searchInput?.value, this.paginator.pageIndex, this.paginator.pageSize, this.sortField, this.sortDirection)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.dataSource = new MatTableDataSource(data['content'])
        this.totalElements = data['totalElements']
      })
  }

  exportar() {
    this.recursoService.listar(this.searchInput?.value, 0, this.totalElements).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      let recursos: Recurso[] = data['content']
      let content = `"#","UNIDADE","CONCURSO","NOME","RECURSO","DATA","PARECER","PARECER-DATA","PARECER-RESPONSÁVEL"\r\n`
      recursos.forEach(row => {
        content += `"${row.id}","${row.concursoCampus}","${row.tituloConcurso.replace(/"/g, "'")}","${row.nome}","${
          row.tipoRecurso
        }","${this.datepipe.transform(row.data, 'dd/MM/YYYY HH:mm:ss')}","${row.parecer}","${
          row.parecerData == null ? '' : this.datepipe.transform(row.parecerData, 'dd/MM/YYYY HH:mm:ss')
        }","${row.parecerUsuario}"\r\n`
      })
      let binaryData = []
      binaryData.push(content)
      let downloadLink = document.createElement('a')
      downloadLink.setAttribute('download', 'recursos.csv')
      downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'attachment/csv;charset=utf-8;' }))
      document.body.appendChild(downloadLink)
      downloadLink.click()
      downloadLink.remove()
    })
  }

  isSuperAdmin(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['SUPERADMIN'])
  }
}
